import { useState, useMemo } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { formatNumeric } from '@/lib/utils/stringUtils'
import AnalyticsTabBar from '@/components/dashboard/AnalyticsTabBar'
import { LoadingState } from '@/components/shared/layout'
import {
  Verdict,
  KpiStrip,
  RaisedCard,
  CardHead,
  SplitBar,
  BarRowsHeader,
  BarRows,
  CardFooterAction,
  CardEmpty,
  type BarRow,
} from '@/components/dashboard/analyticsTabs/TabPrimitives'
import {
  aggregateDeviceTypes,
  getPeriodUniqueVisitorTotal,
  shareOf,
  stackedBarWidths,
} from '@/lib/calculations/breakdownTotals'
import { ChartViewToggle, ShareDonut, foldSlices, type ShareSlice } from '@/components/shared/charts/ShareCharts'
import LocationsSection from './LocationsSection'
import { osDisplayName } from '@/lib/utils/osDisplayName'

type CardView = 'default' | 'donut'

const SPLIT_VIEW_OPTIONS = [
  { value: 'default' as const, label: 'Split bar', icon: 'mix' as const },
  { value: 'donut' as const, label: 'Donut', icon: 'donut' as const },
]
const BARS_VIEW_OPTIONS = [
  { value: 'default' as const, label: 'Ranked bars', icon: 'bars' as const },
  { value: 'donut' as const, label: 'Donut', icon: 'donut' as const },
]

interface DashboardContext {
  propertyId?: string
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
  isLoading?: boolean
}

interface DeviceRow {
  name: string
  count: number
  percentage: number
}

/** "Devices & locations" tab: the devices well, then the locations well. */
export default function DevicesDashboard() {
  const ctx = useOutletContext<DashboardContext>()
  const params = useParams()
  const dashboardData = ctx.dashboardData
  const isLoading = ctx.isLoading
  
  // Get the base path for tab navigation
  const getBasePath = () => {
    if (params.magicId && params.password) return `/s/${params.magicId}/${params.password}`
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    if (params.domain) return `/d/${params.domain}`
    return ''
  }
  const basePath = getBasePath()

  // Process device data from dashboardData. Each card reads one dimension's rows
  // (device type prefers `device_type` rows and only infers from OS rows when there
  // are none, so the same visitors are never counted twice). Shares are taken
  // against the period's deduped unique-visitor total, not a row sum.
  const { osData, deviceData, browserData, totalSessions, deviceCoverage } = useMemo(() => {
    const periodTotal = getPeriodUniqueVisitorTotal(dashboardData?.metrics)
    if (!dashboardData?.metrics?.aggregate) {
      return { osData: [], deviceData: [], browserData: [], totalSessions: periodTotal, deviceCoverage: 0 }
    }

    const aggregate = dashboardData.metrics.aggregate

    const sumBy = (rows: any[], key: string, rename: (v: string) => string = (v) => v): Record<string, number> => {
      const stats: Record<string, number> = {}
      rows.forEach((row: any) => {
        const name = rename(row[key])
        stats[name] = (stats[name] || 0) + (Number(row.count) || 0)
      })
      return stats
    }

    const toRows = (stats: Record<string, number>): DeviceRow[] => {
      const rowSum = Object.values(stats).reduce((sum, n) => sum + n, 0)
      const denominator = periodTotal || rowSum
      return Object.entries(stats)
        .map(([name, count]) => ({ name, count, percentage: shareOf(count, denominator) }))
        .sort((a, b) => b.count - a.count)
    }

    const osArray = toRows(
      sumBy(aggregate.filter((row: any) => row.client_os_aggregate === 'client_os' && row.client_os), 'client_os', osDisplayName),
    )
    const browserArray = toRows(
      sumBy(
        aggregate.filter((row: any) => row.client_browser_aggregate === 'client_browser' && row.client_browser),
        'client_browser',
      ),
    )
    const deviceTypes = aggregateDeviceTypes(aggregate)
    const deviceArray = toRows(
      Object.fromEntries(deviceTypes.map((d) => [d.name.charAt(0).toUpperCase() + d.name.slice(1), d.count])),
    )

    return {
      osData: osArray,
      deviceData: deviceArray,
      browserData: browserArray,
      totalSessions: periodTotal,
      deviceCoverage: deviceTypes.reduce((sum, d) => sum + d.count, 0),
    }
  }, [dashboardData])

  const [showAllOs, setShowAllOs] = useState(false)
  const [showAllBrowsers, setShowAllBrowsers] = useState(false)
  const [deviceView, setDeviceView] = useState<CardView>('default')
  const [osView, setOsView] = useState<CardView>('default')
  const [browserView, setBrowserView] = useState<CardView>('default')
  const rowLimit = 5

  if (isLoading) {
    return (
      <LoadingState
        message="Loading devices and locations..."
        size="lg"
        fullPage
      />
    )
  }

  const topDevice = deviceData[0]
  const topOs = osData[0]
  const toRows = (rows: DeviceRow[]): BarRow[] =>
    rows.map((item) => ({
      key: item.name,
      label: item.name,
      value: formatNumeric(item.count),
      share: item.percentage,
    }))
  // Donut view: top rows plus a folded "Other" (no %), shares against the
  // period's deduped total. What no row covers stays the empty track.
  const toSlices = (rows: DeviceRow[]): ShareSlice[] =>
    foldSlices(rows.map((item) => ({ key: item.name, label: item.name, value: item.count, share: item.percentage })))
  const coverageLine = (rows: DeviceRow[], one: string, many: string) => {
    const covered = rows.reduce((sum, r) => sum + r.count, 0)
    return `Across ${rows.length} ${rows.length === 1 ? one : many} · ${formatNumeric(covered)} of ${formatNumeric(totalSessions)} unique visitors`
  }
  const visibleOs = showAllOs ? osData : osData.slice(0, rowLimit)
  const visibleBrowsers = showAllBrowsers ? browserData : browserData.slice(0, rowLimit)

  return (
    <div className="w-full">
      {/* Analytics Tab Navigation */}
      <AnalyticsTabBar basePath={basePath} className="mb-6" />

      <div className="flex flex-col gap-4">
        <Verdict
          title={
            topDevice
              ? `${topDevice.percentage.toFixed(0)}% of unique visitors browse on ${topDevice.name.toLowerCase()}.${topOs ? ` ${topOs.name} is the top operating system.` : ''}`
              : 'No device data for this period yet.'
          }
          meta="Devices and locations of your unique visitors: device type, operating system, browser and country"
        />

        {/* One well, raised cards (White signal v2) */}
        <div className="well flex flex-col gap-2">
          <KpiStrip
            tiles={[
              { label: 'Unique visitors', value: totalSessions > 0 ? formatNumeric(totalSessions) : '--' },
              { label: 'Device types', value: deviceData.length },
              { label: 'Operating systems', value: osData.length },
              { label: 'Browsers', value: browserData.length },
              { label: 'Top operating system', value: topOs?.name || '--' },
            ]}
          />

          <RaisedCard>
            <CardHead title="Device type">
              <span className="text-xs text-muted-foreground">
                {totalSessions > 0 && deviceCoverage < totalSessions
                  ? `Share of all unique visitors · ${formatNumeric(deviceCoverage)} of ${formatNumeric(totalSessions)} with a detected device`
                  : 'Share of unique visitors'}
              </span>
              <ChartViewToggle options={SPLIT_VIEW_OPTIONS} value={deviceView} onChange={setDeviceView} />
            </CardHead>
            {deviceData.length > 0 && deviceView === 'donut' ? (
              <ShareDonut
                slices={toSlices(deviceData)}
                total={totalSessions}
                totalLabel="unique visitors"
                coverage={coverageLine(deviceData, 'device type', 'device types')}
              />
            ) : deviceData.length > 0 ? (
              <SplitBar
                segments={(() => {
                  const widths = stackedBarWidths(deviceData.map((item) => item.percentage))
                  return deviceData.map((item, i) => ({
                    key: item.name,
                    label: item.name,
                    share: item.percentage,
                    width: widths[i],
                    count: formatNumeric(item.count),
                  }))
                })()}
              />
            ) : (
              <CardEmpty>No device data available</CardEmpty>
            )}
          </RaisedCard>

          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            <RaisedCard>
              <CardHead title="Operating system">
                <ChartViewToggle options={BARS_VIEW_OPTIONS} value={osView} onChange={setOsView} />
              </CardHead>
              {osData.length > 0 && osView === 'donut' ? (
                <ShareDonut
                  slices={toSlices(osData)}
                  total={totalSessions}
                  totalLabel="unique visitors"
                  coverage={coverageLine(osData, 'operating system', 'operating systems')}
                />
              ) : (
                <>
                  <BarRowsHeader label="Operating system" valueLabel="Unique visitors" />
                  {osData.length > 0 ? (
                    <BarRows rows={toRows(visibleOs)} />
                  ) : (
                    <CardEmpty>No operating system data available</CardEmpty>
                  )}
                </>
              )}
              {osView === 'default' && osData.length > rowLimit && (
                <CardFooterAction onClick={() => setShowAllOs((v) => !v)}>
                  {showAllOs ? 'Show fewer' : `Show all ${osData.length} operating systems`}
                </CardFooterAction>
              )}
            </RaisedCard>

            <RaisedCard>
              <CardHead title="Browser">
                <ChartViewToggle options={BARS_VIEW_OPTIONS} value={browserView} onChange={setBrowserView} />
              </CardHead>
              {browserData.length > 0 && browserView === 'donut' ? (
                <ShareDonut
                  slices={toSlices(browserData)}
                  total={totalSessions}
                  totalLabel="unique visitors"
                  coverage={coverageLine(browserData, 'browser', 'browsers')}
                />
              ) : (
                <>
                  <BarRowsHeader label="Browser" valueLabel="Unique visitors" />
                  {browserData.length > 0 ? (
                    <BarRows rows={toRows(visibleBrowsers)} />
                  ) : (
                    <CardEmpty>No browser data available</CardEmpty>
                  )}
                </>
              )}
              {browserView === 'default' && browserData.length > rowLimit && (
                <CardFooterAction onClick={() => setShowAllBrowsers((v) => !v)}>
                  {showAllBrowsers ? 'Show fewer' : `Show all ${browserData.length} browsers`}
                </CardFooterAction>
              )}
            </RaisedCard>
          </div>
        </div>

        {/* Locations (was its own tab): second well, under the same headline */}
        <LocationsSection />
      </div>
    </div>
  )
}
